import cloneDeep from 'lodash/cloneDeep';

import { type DomainMap, type DomainName, type IDomainNode, type IDomainTree } from './types';

let domainTree = {} as IDomainTree;

/**
 * Clones provided domain tree into internal representation
 *
 * @param {object} tree - domain tree configuration
 * @returns {object} the registered tree
 */
export function registerDomains(tree: IDomainTree): IDomainTree {
  domainTree = parseTree(cloneDeep(tree));
  return domainTree;
}

/**
 * Traverse the domain tree from root node to each lead node depth first
 * precalculating parent nodes for future calculations
 *
 * @param {object} rootNode - the root node to parse from
 * @returns {object} the modified tree
 */
export function parseTree(rootNode: IDomainTree): IDomainTree {
  (function traverse(node: IDomainTree | IDomainNode): void {
    if (!node.children) {
      return;
    }
    if (node.children) {
      node.children.map(child => {
        child.parent = node;
        traverse(child);
      });
    }
  })(rootNode);
  return rootNode;
}

/**
 * Resets current domainTree and stops all spawned processes
 */
export function resetDomains(): void {
  const domains = getDomains(domain => !!domain?.module);
  domains.map(domain => domain?.module?.stop());
  domainTree = {} as IDomainTree;
}

/**
 * Returns the current domain tree
 *
 * @returns {object} the loaded domain tree
 */
export function getDomainTree(): IDomainTree {
  return domainTree;
}

/**
 *  Filters provided domains by their name
 *
 * @param {string} name - the name to filter by
 * @returns {Function} - the comparator
 */
export function nameComparator<Name extends DomainName>(name: Name) {
  return function comparator(domain: IDomainNode): boolean {
    return domain.name === name;
  };
}

/**
 *  Returns BFS (Breadth First) ordered array
 *
 * @param {object} rootNode - the node to start search from
 * @returns {Array} - BFS ordered array
 */
export function getBFSTraversal(rootNode: IDomainNode = domainTree): IDomainNode[] {
  const queue: IDomainNode[] = [];

  (function traverse(level: IDomainNode[]): void {
    queue.push(...level);

    const nextLevel: IDomainNode[] = [];
    level.forEach(node => {
      if (node.children) {
        nextLevel.push(...node.children);
      }
    });
    if (nextLevel.length > 0) {
      traverse(nextLevel);
    }
  })([rootNode]);

  return queue;
}

/**
 * Returns DFS (Depth First Search) ordered array
 *
 * @param {object} rootNode - the node to start search from
 * @returns {Array} - DFS ordered array
 */
export function getDFSTraversal(rootNode = domainTree): IDomainNode[] {
  const queue: IDomainNode[] = [];
  (function traverse(node: IDomainNode | IDomainTree): void {
    queue.push(node);
    if (node.children) {
      node.children.map(child => traverse(child));
    }
  })(rootNode);
  return queue;
}

/**
 * Return the first found domain given the provided comparator
 *
 * @param {Function} comparator - comparator to find domain with
 * @param {object} rootNode - the node to start search from
 * @returns {object} the found domain
 */
export function findDomain<Name extends DomainName>(
  comparator: (node: IDomainNode<DomainName>) => boolean,
  rootNode = domainTree,
): IDomainNode<Name> {
  return getBFSTraversal(rootNode).find(comparator) as IDomainNode<Name>;
}

/**
 * Registers a domain by adding its loaded domainModule to the registry
 *
 * @param {string} domainName - the name of the domain to register
 * @param {object} domainModule
 * @param {object} rootNode - the node to start search from
 */
export function addDomain<Name extends DomainName>(
  domainName: Name,
  domainModule: DomainMap[Name],
  rootNode = domainTree,
): DomainMap[Name] {
  const node = getDomain(domainName, rootNode);

  if (node) {
    node.module = domainModule;
  }

  return domainModule;
}

/**
 * Return the first domain that matches the provided name
 *
 * @param {string} domainName - the name of the domain to find
 * @param {object} rootNode - the node to start search from
 * @returns {object} found domain
 */
export function getDomain<Name extends DomainName>(
  domainName: Name,
  rootNode = domainTree,
): IDomainNode<Name> {
  return findDomain(nameComparator(domainName), rootNode);
}

/**
 * Return all domains that match comparator
 *
 * @param {Function} comparator - comparator to match domains with
 * @param {object} rootNode - the node to start search from
 * @returns {Array} - BFS ordered array of filtered domains
 */
export function getDomains(
  comparator: (domain: IDomainNode) => boolean,
  rootNode = domainTree,
): IDomainNode[] {
  return getBFSTraversal(rootNode).filter(comparator);
}
